<template>
  <v-row id="order-chat-history" class="g-container-large" no-gutters>
    <v-col cols="12">
      <i-overlay v-if="loading" text="Loading Retailer messages..." />
      <template v-else>
        <v-row no-gutters align="center" class="mb-4">
          <v-col>
            <h2 class="ma-0">Retailer message history</h2>
            <div class="p-order-chat-history__subtitle">
              Retained Retailer messages across this supplier account.
            </div>
          </v-col>
          <v-col cols="12" md="4" class="pt-3 pt-md-0">
            <v-text-field
              v-model="search"
              outlined
              dense
              hide-details
              prepend-inner-icon="mdi-magnify"
              label="Search order, retailer or message"
            />
          </v-col>
        </v-row>
        <v-alert v-if="!conversations.length" type="info" outlined>
          There are no retained Retailer messages for this supplier.
        </v-alert>
        <v-card v-else class="i-card">
          <v-data-table
            :headers="headers"
            :items="conversations"
            :search="search"
            :items-per-page="25"
            class="i-data-table"
          >
            <template #[`item.supplierOrderNumber`]="{ item }"
              ><v-btn
                text
                color="primary"
                small
                :disabled="!orderChatEnabled"
                @click="openChat(item)"
                >{{ item.supplierOrderNumber }}</v-btn
              ></template
            >
            <template #[`item.storeName`]="{ item }">{{
              item.storeName || 'Retailer'
            }}</template>
            <template #[`item.lastMessage`]="{ item }"
              ><span
                v-if="item.lastMessage"
                class="p-order-chat-history__preview"
                ><strong>{{ item.lastMessage.senderName }}</strong>
                {{ item.lastMessage.body }}</span
              ><span v-else>None yet</span></template
            >
            <template #[`item.unreadCount`]="{ item }"
              ><v-chip
                v-if="item.unreadCount"
                small
                color="orange"
                text-color="white"
                >{{ item.unreadCount }}</v-chip
              ><span v-else>Read</span></template
            >
            <template #[`item.expiresAt`]="{ item }"
              ><span v-if="item.expiresAt"
                >{{ formatDateTime(item.expiresAt) }} ({{
                  daysRemaining(item.expiresAt)
                }}
                days)</span
              ><span v-else>-</span></template
            >
            <template #[`item.actions`]="{ item }"
              ><v-btn
                v-if="orderChatEnabled"
                icon
                :color="conversationReadOnly(item) ? 'grey' : 'secondary'"
                aria-label="Open Retailer messages"
                v-tooltip="'Open Retailer messages'"
                @click="openChat(item)"
              >
                <v-badge color="orange" dot overlap :value="!!item.unreadCount">
                  <v-icon>mdi-message-text-outline</v-icon>
                </v-badge>
              </v-btn>
              <v-btn
                icon
                color="secondary"
                aria-label="Download Retailer message transcript"
                v-tooltip="'Download transcript'"
                @click="downloadTranscript(item)"
                ><v-icon>mdi-file-pdf-box</v-icon></v-btn
              ></template
            >
          </v-data-table>
        </v-card>
      </template>

      <v-dialog
        v-if="orderChatEnabled"
        v-model="chatOpen"
        max-width="800"
        scrollable
      >
        <v-card class="p-support-history-dialog">
          <v-toolbar
            flat
            color="primary"
            dark
            class="p-support-history-dialog__header"
          >
            <v-avatar size="32" class="mr-3" color="secondary">
              <v-icon small>mdi-store-outline</v-icon>
            </v-avatar>
            <div>
              <v-toolbar-title>Retailer messages</v-toolbar-title>
              <div class="p-support-history-dialog__order">
                PO
                {{
                  selectedConversation &&
                  selectedConversation.supplierOrderNumber
                }}
              </div>
            </div>
            <v-spacer />
            <v-btn
              icon
              aria-label="Close Retailer messages"
              @click="chatOpen = false"
            >
              <v-icon>mdi-close</v-icon>
            </v-btn>
          </v-toolbar>
          <div
            v-if="showRetentionMessage"
            class="p-support-history-dialog__retention px-5 py-3"
          >
            <v-icon small color="secondary" class="mr-2"
              >mdi-clock-outline</v-icon
            >
            {{ retentionMessage }}
          </div>
          <div
            v-if="disabledReasonMessage"
            class="p-support-history-dialog__alert px-5 py-3"
          >
            <v-icon small class="mr-2">mdi-alert-circle-outline</v-icon>
            {{ disabledReasonMessage }}
          </div>
          <v-divider />
          <v-card-text
            ref="messages"
            class="p-support-history-dialog__messages px-5 py-5"
          >
            <div
              v-for="message in conversation.messages"
              :key="message.id"
              class="p-support-history-dialog__message mb-4"
              :class="{
                'p-support-history-dialog__message--own':
                  message.senderType === 'SUPPLIER'
              }"
            >
              <div class="p-support-history-dialog__message-meta mb-1">
                <strong>{{ message.senderName }}</strong>
                <span>{{ formatDateTime(message.createdAt) }}</span>
              </div>
              <div class="p-support-history-dialog__bubble">
                {{ message.body }}
              </div>
            </div>
            <div v-if="!conversation.messages.length" class="text-center py-10">
              <v-icon color="secondary" size="34"
                >mdi-message-text-outline</v-icon
              >
              <div class="mt-3">
                Start a conversation about this purchase order.
              </div>
            </div>
          </v-card-text>
          <v-divider />
          <div class="px-5 pt-4 pb-5">
            <v-textarea
              v-model="newMessage"
              label="Message retailer"
              outlined
              rows="3"
              auto-grow
              :counter="4000"
              :disabled="selectedConversationReadOnly"
              hide-details="auto"
            />
            <div class="d-flex justify-end mt-3">
              <v-btn
                class="i-btn-block-secondary"
                :disabled="selectedConversationReadOnly || !newMessage.trim()"
                @click="sendMessage"
                >Send message<v-icon right>mdi-send</v-icon></v-btn
              >
            </div>
          </div>
        </v-card>
      </v-dialog>
    </v-col>
  </v-row>
</template>

<script>
import { mapActions, mapGetters, mapState } from 'vuex'
import iOverlay from '@/components/Overlay'
import { FTWebSocket } from '@/utils/FTWebSocket'
import { formatUtcDateTime } from '@/utils/timezone'
import {
  downloadOrderConversationTranscript,
  fetchOrderConversation,
  fetchOrderConversationHistory,
  sendOrderConversationMessage
} from '@/services/orderConversations'

export default {
  components: { iOverlay },
  data: () => ({
    loading: true,
    conversations: [],
    chatOpen: false,
    selectedConversation: null,
    conversation: { messages: [] },
    newMessage: '',
    search: '',
    headers: [
      { text: 'Order', value: 'supplierOrderNumber' },
      { text: 'Retailer', value: 'storeName' },
      { text: 'Last message', value: 'lastMessage' },
      { text: 'Unread', value: 'unreadCount' },
      { text: 'Retained until', value: 'expiresAt' },
      { text: '', value: 'actions', sortable: false }
    ]
  }),
  computed: {
    ...mapState('preferences', ['selectedSupplierId']),
    ...mapGetters('tenant', ['orderChatEnabled', 'supportEmail']),
    selectedConversationReadOnly() {
      return (
        !!this.selectedConversation?.expiresAt ||
        !!this.selectedConversationDisabledReason
      )
    },
    selectedConversationDisabledReason() {
      return (
        this.conversation.messageDisabledReason ||
        this.selectedConversation?.messageDisabledReason
      )
    },
    supportContact() {
      return this.supportEmail || 'support'
    },
    disabledReasonMessage() {
      if (this.selectedConversationDisabledReason === 'SUPPLIER_SUSPENDED') {
        return `Your supplier account is suspended and messages cannot be sent. Please contact support at ${this.supportContact}.`
      }
      if (this.selectedConversationDisabledReason === 'RETAILER_CREDIT_HOLD') {
        return `This retailer is on credit hold and messages cannot be sent. Please contact support at ${this.supportContact}.`
      }
      return ''
    },
    retentionMessage() {
      if (this.selectedConversationDisabledReason) {
        return 'Message history remains available, but replies are currently disabled.'
      }
      return this.selectedConversationReadOnly
        ? 'Messages are not available after delivery is confirmed. Retailers can raise an Order Query or Credit Return for any issues.'
        : 'Messages are available until delivery is confirmed.'
    },
    showRetentionMessage() {
      return (
        !!this.selectedConversationDisabledReason ||
        this.selectedConversationReadOnly
      )
    }
  },
  methods: {
    ...mapActions('notifications', ['fetchActionableSupportMessages']),
    formatDateTime(value) {
      return formatUtcDateTime(value, 'DD/MM/YYYY h:mm a', '-')
    },
    daysRemaining(expiresAt) {
      return Math.max(
        0,
        Math.ceil((new Date(expiresAt).getTime() - Date.now()) / 86400000)
      )
    },
    loadHistory() {
      this.loading = true
      return fetchOrderConversationHistory(this.selectedSupplierId)
        .then((items) => {
          this.conversations = items
        })
        .finally(() => {
          this.loading = false
        })
    },
    openChat(item) {
      if (!this.orderChatEnabled) return
      this.selectedConversation = item
      this.markConversationRead(item.supplierOrderId)
      this.conversation = {
        messages: [],
        messageDisabledReason: item.messageDisabledReason || null
      }
      this.chatOpen = true
      fetchOrderConversation(
        item.supplierOrderId,
        this.selectedSupplierId
      ).then((conversation) => {
        this.conversation = conversation
        this.markConversationRead(item.supplierOrderId)
        this.fetchActionableSupportMessages(this.selectedSupplierId)
        this.scrollToLatestMessage()
      })
    },
    markConversationRead(supplierOrderId) {
      this.conversations = this.conversations.map((conversation) =>
        conversation.supplierOrderId === supplierOrderId
          ? { ...conversation, unreadCount: 0 }
          : conversation
      )
      if (this.selectedConversation?.supplierOrderId === supplierOrderId) {
        this.selectedConversation = {
          ...this.selectedConversation,
          unreadCount: 0
        }
      }
    },
    conversationReadOnly(item) {
      return !!item.expiresAt || !!item.messageDisabledReason
    },
    sendMessage() {
      if (!this.orderChatEnabled || this.selectedConversationReadOnly) return
      sendOrderConversationMessage(
        this.selectedConversation.supplierOrderId,
        this.selectedSupplierId,
        this.newMessage
      ).then((conversation) => {
        this.conversation = conversation
        this.newMessage = ''
        this.scrollToLatestMessage()
        this.loadHistory()
      })
    },
    downloadTranscript(item) {
      downloadOrderConversationTranscript(
        item.supplierOrderId,
        this.selectedSupplierId
      ).then((blob) => {
        const url = URL.createObjectURL(blob)
        const link = document.createElement('a')
        link.href = url
        link.download = `order-chat-${item.supplierOrderNumber}.pdf`
        link.click()
        URL.revokeObjectURL(url)
      })
    },
    scrollToLatestMessage() {
      this.$nextTick(() => {
        const messages = this.$refs.messages?.$el || this.$refs.messages
        if (messages) messages.scrollTop = messages.scrollHeight
      })
    }
  },
  mounted() {
    this.loadHistory()
    FTWebSocket.subscribe('/user/queue/order-chat/message', (event) => {
      this.loadHistory()
      if (this.selectedConversation?.supplierOrderId === event.supplierOrderId)
        this.openChat(this.selectedConversation)
      this.$store.dispatch('notifications/addNotification', {
        color: 'info',
        text: `New Retailer message from ${event.senderName}`
      })
    })
  }
}
</script>

<style lang="scss" scoped>
.p-support-history-dialog {
  &__header {
    min-height: 72px;
  }

  &__order,
  &__retention,
  &__message-meta {
    font-size: 0.75rem;
  }

  &__retention {
    align-items: center;
    display: flex;
    opacity: 0.78;
  }

  &__alert {
    align-items: center;
    background: #fff7e6;
    color: #5d3d00;
    display: flex;
  }

  &__messages {
    max-height: 500px;
    min-height: 320px;
    overflow-y: auto;
  }

  &__message {
    max-width: 78%;
  }

  &__message--own {
    margin-left: auto;
  }

  &__message-meta {
    display: flex;
    gap: 12px;
    justify-content: space-between;
    opacity: 0.72;
  }

  &__bubble {
    background: rgba(0, 0, 0, 0.06);
    border-radius: 8px;
    padding: 12px 14px;
    white-space: pre-wrap;
  }

  &__message--own &__bubble {
    background: $primary-color;
    color: white;
  }
}

.p-order-chat-history {
  &__subtitle {
    opacity: 0.72;
  }

  &__preview {
    display: inline-block;
    max-width: 340px;
    overflow: hidden;
    text-overflow: ellipsis;
    vertical-align: middle;
    white-space: nowrap;
  }
}
</style>
